import { ref, computed } from "vue";
import * as tus from "tus-js-client";
import { askIQStream } from "@/api/askIQApi";
import {
  completeMatchedUploadTask,
  confirmProposedVideo,
  generateVideoUploadUrl,
  getCloudflareVideoStatus,
} from "@/api/proficiencyAPI";
import {
  changeCheckpointV2,
  getCheckpointVideoSignedUrl,
  getResponsibilityCheckpointDetailV2,
  getResponsibilityIdV2,
} from "@/api/responsibilityAPI";
import configService from "@/services/config";
import { useNotificationStore } from "@/store/pinia/notifications";
import {
  buildContextValidationSuggestedInstructions,
  buildClearContextValidationMismatchPatch,
  isContextValidationFail,
  isContextVerificationFailed,
} from "@/utils/buildContextValidationSuggestedInstructions";
import { buildVideoToComponentsCheckpointPatch } from "@/utils/buildVideoToComponentsCheckpointPatch";
import {
  buildIqVideoSuggestEndpoint,
  buildVideoToComponentsPayload,
} from "@/utils/buildVideoToComponentsPayload";
import {
  isPlayableSignedVideoUrl,
  pollCloudflareStreamStatus,
} from "@/utils/videoPostUploadReveal";

const VIDEO_TO_COMPONENTS_TIMEOUT_MS = 6 * 60 * 1000;
const SIGNED_URL_MAX_ATTEMPTS = 30;
const SIGNED_URL_DELAY_MS = 1000;

function getAccountId() {
  try {
    return JSON.parse(localStorage.getItem("currentAccountID"));
  } catch {
    return localStorage.getItem("currentAccountID");
  }
}

function extractVideoUrlFromIframeHtml(iframeHtml) {
  if (!iframeHtml) return "";
  if (!String(iframeHtml).includes("<iframe") && !String(iframeHtml).includes("src=")) {
    return String(iframeHtml);
  }
  const srcMatch = String(iframeHtml).match(/src=\\?"([^"\\]+)/);
  if (srcMatch && srcMatch[1]) return srcMatch[1];
  const fallbackMatch = String(iframeHtml).match(/src="([^"]+)"/);
  if (fallbackMatch && fallbackMatch[1]) return fallbackMatch[1];
  return "";
}

function formatPrefixDisplay(prefix) {
  const value = String(prefix || "").trim();
  if (!value) return "";
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function generateVideoThumbnail(file) {
  return new Promise((resolve) => {
    try {
      const video = document.createElement("video");
      const url = URL.createObjectURL(file);
      video.src = url;
      video.muted = true;
      video.playsInline = true;
      video.currentTime = 1;
      video.addEventListener("seeked", () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = video.videoWidth || 640;
          canvas.height = video.videoHeight || 360;
          canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
          URL.revokeObjectURL(url);
          canvas.toBlob((blob) => resolve(blob), "image/jpeg", 0.85);
        } catch (e) {
          URL.revokeObjectURL(url);
          resolve(null);
        }
      });
      video.addEventListener("error", () => {
        URL.revokeObjectURL(url);
        resolve(null);
      });
      if (typeof video.load === "function") {
        video.load();
      }
    } catch (e) {
      resolve(null);
    }
  });
}

/**
 * Task-page UploadVideoWizard flow: TUS upload, Cloudflare ready, signed URL,
 * IQ /video_to_components/ (PASS/FAIL). On PASS, complete-matched-upload-task
 * then reload tasks. Does not call the backend video-to-components proxy.
 */
export function useTaskVideoUpload({ onSuccess } = {}) {
  const notificationStore = useNotificationStore();

  const showUploadWizard = ref(false);
  const uploadWizardState = ref("upload");
  const uploadWizardFile = ref(null);
  const uploadWizardProgress = ref(0);
  const uploadTask = ref(null);
  const resultMessage = ref("");
  const uploadWizardIqGenerationStatus = ref("connecting");
  /** Tasks upload: show IQ panel chrome only — no streamed thinking / JSON trace lines. */
  const uploadWizardIqTraceEntries = computed(() => []);
  const uploadWizardIqGenerationInProgress = computed(
    () =>
      uploadWizardIqGenerationStatus.value === "connecting" ||
      uploadWizardIqGenerationStatus.value === "streaming"
  );

  let activeTusUpload = null;
  let activeTusReject = null;
  let uploadCancelled = false;
  let flowToken = 0;
  let validationAbortController = null;
  let pendingVideoId = null;
  let pendingThumbnail = null;
  let pendingCheckpoint = null;
  let pendingResponsibility = null;

  function resetWizard() {
    uploadWizardState.value = "upload";
    uploadWizardFile.value = null;
    uploadWizardProgress.value = 0;
    resultMessage.value = "";
    uploadWizardIqGenerationStatus.value = "connecting";
  }

  function beginValidationThinkingStream() {
    uploadWizardIqGenerationStatus.value = "connecting";
  }

  function updateValidationThinkingStream() {
    if (uploadWizardIqGenerationStatus.value === "connecting") {
      uploadWizardIqGenerationStatus.value = "streaming";
    }
  }

  function endValidationThinkingStream() {
    uploadWizardIqGenerationStatus.value = "connecting";
  }

  function abortValidation() {
    if (validationAbortController) {
      validationAbortController.abort();
      validationAbortController = null;
    }
  }

  function abortActiveTusUpload() {
    uploadCancelled = true;
    const upload = activeTusUpload;
    activeTusUpload = null;
    if (upload) {
      try {
        upload.abort();
      } catch (e) {
        // ignore abort errors
      }
    }
    if (activeTusReject) {
      activeTusReject(Object.assign(new Error("Upload cancelled"), { cancelled: true }));
      activeTusReject = null;
    }
  }

  function cancelInFlightWork() {
    flowToken += 1;
    abortActiveTusUpload();
    abortValidation();
    pendingVideoId = null;
    pendingThumbnail = null;
    pendingCheckpoint = null;
    pendingResponsibility = null;
  }

  function openUploadVideoWizard(task) {
    cancelInFlightWork();
    uploadCancelled = false;
    uploadTask.value = task || null;
    resetWizard();
    showUploadWizard.value = true;
  }

  function closeUploadWizard() {
    cancelInFlightWork();
    showUploadWizard.value = false;
    uploadTask.value = null;
    resetWizard();
  }

  function isBlockingCloseState() {
    return ["progress", "processing", "validating"].includes(uploadWizardState.value);
  }

  function handleUploadWizardAbort() {
    closeUploadWizard();
  }

  function onUploadWizardInput(open) {
    if (isBlockingCloseState() && !open) {
      showUploadWizard.value = true;
      return;
    }
    if (open) {
      showUploadWizard.value = true;
      return;
    }
    closeUploadWizard();
  }

  function handleUploadWizardGoBack() {
    if (isBlockingCloseState()) {
      showUploadWizard.value = true;
      return;
    }
    closeUploadWizard();
  }

  function handleUploadWizardClose() {
    if (isBlockingCloseState()) {
      showUploadWizard.value = true;
      return;
    }
    closeUploadWizard();
  }

  function handleUploadWizardSelect({ file, tooLong }) {
    if (tooLong) {
      uploadWizardState.value = "too-long";
      uploadWizardFile.value = file || null;
      return;
    }
    uploadWizardFile.value = file || null;
    uploadWizardState.value = file ? "selected" : "upload";
  }

  function handleUploadWizardRemove() {
    abortActiveTusUpload();
    uploadWizardFile.value = null;
    uploadWizardState.value = "upload";
    uploadWizardProgress.value = 0;
  }

  function handleUploadWizardDone() {
    closeUploadWizard();
    if (typeof onSuccess === "function") {
      onSuccess();
    }
  }

  async function loadCheckpointContext(accountId, task) {
    const responsibilityId = task?.responsibilityId ?? task?.responsibility_id;
    const checkpointId = task?.checkpointId ?? task?.checkpoint_id;
    if (accountId == null || responsibilityId == null || checkpointId == null) {
      throw new Error("Missing account, responsibility, or checkpoint for video upload");
    }

    const [checkpointRes, responsibilityRes] = await Promise.all([
      getResponsibilityCheckpointDetailV2(accountId, responsibilityId, checkpointId),
      getResponsibilityIdV2(accountId, responsibilityId, "responsibility").catch(() => ({
        data: {},
      })),
    ]);

    pendingCheckpoint = checkpointRes?.data || {};
    pendingResponsibility = responsibilityRes?.data || {};
    return { responsibilityId, checkpointId };
  }

  async function uploadVideoFileViaCloudflareTus(accountId, checkpointId, file) {
    const setProgress = (n) => {
      uploadWizardProgress.value = Math.max(0, Math.min(100, Math.round(Number(n) || 0)));
    };

    setProgress(0);
    const thumbnail = await generateVideoThumbnail(file);
    if (uploadCancelled) {
      throw Object.assign(new Error("Upload cancelled"), { cancelled: true });
    }

    const urlRes = await generateVideoUploadUrl(accountId, checkpointId, file.size);
    if (uploadCancelled) {
      throw Object.assign(new Error("Upload cancelled"), { cancelled: true });
    }
    const uploadUrl = urlRes.data?.upload_url;
    const videoId = urlRes.data?.video_id;
    if (!uploadUrl || !videoId) {
      throw new Error("Missing Cloudflare upload URL or video id");
    }

    await new Promise((resolve, reject) => {
      let settled = false;
      const safeResolve = () => {
        if (settled) return;
        settled = true;
        activeTusReject = null;
        activeTusUpload = null;
        resolve();
      };
      const safeReject = (err) => {
        if (settled) return;
        settled = true;
        activeTusReject = null;
        activeTusUpload = null;
        reject(err);
      };

      const upload = new tus.Upload(file, {
        uploadUrl,
        chunkSize: 256 * 1024 * 20,
        storeFingerprintForResuming: false,
        retryDelays: [0, 3000, 5000, 10000],
        onProgress: (bytesUploaded, bytesTotal) => {
          if (uploadCancelled) return;
          setProgress(Math.round((bytesUploaded / bytesTotal) * 90));
        },
        onSuccess: () => {
          if (uploadCancelled) {
            safeReject(Object.assign(new Error("Upload cancelled"), { cancelled: true }));
            return;
          }
          safeResolve();
        },
        onError: (err) => {
          if (uploadCancelled) {
            safeReject(Object.assign(err || new Error("Upload cancelled"), { cancelled: true }));
            return;
          }
          safeReject(err);
        },
      });
      activeTusUpload = upload;
      activeTusReject = safeReject;
      upload.start();
    });

    if (uploadCancelled) {
      throw Object.assign(new Error("Upload cancelled"), { cancelled: true });
    }

    setProgress(100);
    return { videoId, thumbnail };
  }

  async function ensurePendingVideoLinkedOnCheckpoint(
    accountId,
    responsibilityId,
    checkpointId,
    videoId
  ) {
    const existing =
      pendingCheckpoint?.microlearning_cloudflare_video_id ||
      pendingCheckpoint?.microlearning_video_id ||
      pendingCheckpoint?.microlearning_id;
    if (existing != null && String(existing) === String(videoId)) {
      return;
    }
    await changeCheckpointV2(accountId, responsibilityId, checkpointId, {
      microlearning_cloudflare_video_id: videoId,
      microlearning_video_id: videoId,
      microlearning: "Video is being processed. It will be available in a moment.",
    });
  }

  async function waitUntilCloudflareVideoReady(videoId, token) {
    if (!videoId) return false;
    const { ready } = await pollCloudflareStreamStatus({
      uid: videoId,
      pollMs: 1000,
      maxMs: 10 * 60 * 1000,
      fetcher: async (id) => {
        const res = await getCloudflareVideoStatus(id);
        return res?.data ?? null;
      },
    });
    return token === flowToken && ready;
  }

  async function pollAndPersistPlayableSignedUrl(accountId, responsibilityId, checkpointId, token) {
    for (let attempt = 0; attempt < SIGNED_URL_MAX_ATTEMPTS; attempt++) {
      if (token !== flowToken) return null;
      try {
        const signedUrlRes = await getCheckpointVideoSignedUrl(
          accountId,
          responsibilityId,
          checkpointId
        );
        const url = signedUrlRes?.data?.url ?? null;
        if (isPlayableSignedVideoUrl(url)) {
          await changeCheckpointV2(accountId, responsibilityId, checkpointId, {
            proposed_cloudflarevideo_id: url,
            microlearning: url,
          });
          return url;
        }
      } catch (err) {
        console.error("[useTaskVideoUpload] Error polling signed URL:", err);
      }
      await new Promise((resolve) => setTimeout(resolve, SIGNED_URL_DELAY_MS));
    }
    return null;
  }

  function buildCheckpointCriteria() {
    const prefix = formatPrefixDisplay(pendingCheckpoint?.prefix);
    const criteria = pendingCheckpoint?.criteria || uploadTask.value?.criteria || "";
    return `${prefix} ${criteria}`.trim();
  }

  async function confirmPendingProposedVideo(accountId, responsibilityId, checkpointId, signedUrl) {
    if (!pendingVideoId) return null;
    const formData = new FormData();
    formData.append("video_id", pendingVideoId);
    if (pendingThumbnail) {
      formData.append("thumbnail", pendingThumbnail, "thumbnail.jpg");
    }
    const confirmRes = await confirmProposedVideo(accountId, checkpointId, formData);
    const proposedThumbnail = confirmRes.data?.proposed_video_source_thumbnail ?? null;
    await changeCheckpointV2(accountId, responsibilityId, checkpointId, {
      microlearning_cloudflare_video_id: pendingVideoId,
      microlearning_video_id: pendingVideoId,
      microlearning: signedUrl || "Video is being processed. It will be available in a moment.",
      ...(signedUrl && { proposed_cloudflarevideo_id: signedUrl }),
      ...(proposedThumbnail && { microlearning_video_thumbnail: proposedThumbnail }),
    });
    return confirmRes.data;
  }

  async function persistValidationResult(
    accountId,
    responsibilityId,
    checkpointId,
    suggestedInstructions,
    mode
  ) {
    try {
      const patch =
        mode === "pass"
          ? buildClearContextValidationMismatchPatch(pendingCheckpoint?.proposed_instructions)
          : buildVideoToComponentsCheckpointPatch({
              suggestedInstructions,
              mode,
              microlearningSelection: null,
            });
      await changeCheckpointV2(accountId, responsibilityId, checkpointId, patch);
      return true;
    } catch (err) {
      console.error("[useTaskVideoUpload] persist video_to_components result:", err);
      return false;
    }
  }

  async function runContextValidationRequest({
    signedUrl,
    responsibilityName,
    responsibilityPurpose,
  }) {
    abortValidation();
    validationAbortController = new AbortController();

    const currentAccountDetails = JSON.parse(localStorage.getItem("currentAccountDetails") || "{}");
    const businessPurpose = currentAccountDetails.purpose || "";
    const extractedVideoUrl = extractVideoUrlFromIframeHtml(signedUrl) || signedUrl;
    const customPayload = buildVideoToComponentsPayload({
      videoUrl: extractedVideoUrl,
      businessPurpose,
      responsibilityName: responsibilityName || "",
      responsibilityPurpose: responsibilityPurpose || "",
      checkpointCriteria: buildCheckpointCriteria(),
      language: "english",
      microlearningSelection: null,
    });
    const apiEndpoint = buildIqVideoSuggestEndpoint(configService.get("iqBaseUrl"), true);

    const res = await askIQStream({
      question: "Validate whether this video matches the checkpoint context.",
      signal: validationAbortController.signal,
      timeoutMs: VIDEO_TO_COMPONENTS_TIMEOUT_MS,
      customEndpoint: apiEndpoint,
      customPayload,
      onThinkingUpdate: updateValidationThinkingStream,
    });
    return buildContextValidationSuggestedInstructions(res?.data, extractedVideoUrl);
  }

  async function runPostUploadContextValidation({
    accountId,
    responsibilityId,
    checkpointId,
    videoId,
    token,
  }) {
    uploadWizardState.value = "processing";

    await ensurePendingVideoLinkedOnCheckpoint(accountId, responsibilityId, checkpointId, videoId);
    if (token !== flowToken) return;

    const ready = await waitUntilCloudflareVideoReady(videoId, token);
    if (token !== flowToken) return;
    if (!ready) {
      notificationStore.error("Could not finish processing the video. Please try again.");
      uploadWizardState.value = "error";
      return;
    }

    const signedUrl = await pollAndPersistPlayableSignedUrl(
      accountId,
      responsibilityId,
      checkpointId,
      token
    );
    if (token !== flowToken) return;
    if (!signedUrl) {
      notificationStore.error("Could not get a playable video URL. Please try again.");
      uploadWizardState.value = "error";
      return;
    }

    const responsibilityName =
      pendingResponsibility?.name || uploadTask.value?.responsibility || "";
    const responsibilityPurpose = pendingResponsibility?.purpose || "";

    uploadWizardState.value = "validating";
    beginValidationThinkingStream();

    let suggestedInstructions;
    try {
      suggestedInstructions = await runContextValidationRequest({
        signedUrl,
        responsibilityName,
        responsibilityPurpose,
      });
    } catch (err) {
      if (
        err?.name === "AbortError" ||
        err?.name === "CanceledError" ||
        err?.code === "ERR_CANCELED"
      ) {
        return;
      }
      if (token !== flowToken) return;
      console.error("[useTaskVideoUpload] IQ video-to-components request failed:", err);
      notificationStore.error("Could not verify this video against the checkpoint context.");
      uploadWizardState.value = "error";
      endValidationThinkingStream();
      return;
    }

    endValidationThinkingStream();
    if (token !== flowToken) return;

    await confirmPendingProposedVideo(accountId, responsibilityId, checkpointId, signedUrl);
    if (token !== flowToken) return;

    if (isContextVerificationFailed(suggestedInstructions)) {
      notificationStore.error("Could not verify this video against the checkpoint context.");
      uploadWizardState.value = "error";
      return;
    }

    if (isContextValidationFail(suggestedInstructions)) {
      resultMessage.value =
        suggestedInstructions?.error ||
        suggestedInstructions?.validation?.reasoning ||
        "Upload a different video and try again.";
      const saved = await persistValidationResult(
        accountId,
        responsibilityId,
        checkpointId,
        suggestedInstructions,
        "fail"
      );
      if (!saved) {
        notificationStore.error("Could not save video validation result.");
      }
      uploadWizardState.value = "not-related";
      return;
    }

    const saved = await persistValidationResult(
      accountId,
      responsibilityId,
      checkpointId,
      suggestedInstructions,
      "pass"
    );
    if (!saved) {
      notificationStore.error("Could not save video validation result.");
    }

    try {
      await completeMatchedUploadTask(accountId, responsibilityId, checkpointId, {
        video_ref: pendingVideoId,
        video_url: signedUrl,
      });
    } catch (err) {
      if (token !== flowToken) return;
      console.error("[useTaskVideoUpload] complete-matched-upload-task failed:", err);
      notificationStore.error("Could not complete the video upload task.");
      uploadWizardState.value = "error";
      return;
    }

    if (token !== flowToken) return;
    if (typeof onSuccess === "function") {
      onSuccess();
    }
    resultMessage.value = "";
    uploadWizardState.value = "related";
  }

  async function handleUploadWizardSubmit(file) {
    if (!file || uploadWizardState.value === "progress") return;

    uploadCancelled = false;
    uploadWizardFile.value = file;
    uploadWizardState.value = "progress";
    uploadWizardProgress.value = 0;
    resultMessage.value = "";

    const token = (flowToken += 1);
    const accountId = getAccountId();
    const task = uploadTask.value;

    try {
      const { responsibilityId, checkpointId } = await loadCheckpointContext(accountId, task);
      if (uploadCancelled || token !== flowToken) return;

      const { videoId, thumbnail } = await uploadVideoFileViaCloudflareTus(
        accountId,
        checkpointId,
        file
      );
      if (uploadCancelled || token !== flowToken) return;

      pendingVideoId = videoId;
      pendingThumbnail = thumbnail;
      await runPostUploadContextValidation({
        accountId,
        responsibilityId,
        checkpointId,
        videoId,
        token,
      });
    } catch (err) {
      if (uploadCancelled || err?.cancelled || token !== flowToken) {
        return;
      }
      console.error("[useTaskVideoUpload] UploadVideoWizard upload failed:", err);
      uploadWizardState.value = "error";
      uploadWizardProgress.value = 0;
      notificationStore.error("Upload failed. Please try again.");
    }
  }

  return {
    showUploadWizard,
    uploadWizardState,
    uploadWizardFile,
    uploadWizardProgress,
    uploadTask,
    resultMessage,
    uploadWizardIqGenerationStatus,
    uploadWizardIqTraceEntries,
    uploadWizardIqGenerationInProgress,
    openUploadVideoWizard,
    closeUploadWizard,
    onUploadWizardInput,
    handleUploadWizardGoBack,
    handleUploadWizardClose,
    handleUploadWizardSelect,
    handleUploadWizardRemove,
    handleUploadWizardSubmit,
    handleUploadWizardDone,
    handleUploadWizardAbort,
  };
}
